import Lock from 'browser-tabs-lock';

import {
  bufferToBase64UrlEncoded,
  createQueryParams,
  createRandomString,
  encode,
  getBaseUrl,
  openPopup,
  parseQueryResult,
  runIframe,
  runPopup,
  sha256,
  validateCrypto,
  validateOptions
} from './utils';

import { oauthToken } from './api';

import { getUniqueScopes } from './scope';

import {
  CacheKey,
  CacheManager,
  ICache,
  InMemoryCache,
  LocalStorageCache,
  SessionStorageCache
} from './cache';

import {
  AuthenticationError,
  GenericError,
  PopupCancelledError,
  TimeoutError
} from './errors';
import { verify as verifyToken } from './jwt';

import {
  BASE_JITTER,
  BLOCKED_REFRESH_TOKEN_CALL_ERROR,
  BLOCK_REFRESH_TOKEN_CALL_ATTEMPTS,
  CACHE_LOCATION_MEMORY,
  DEFAULT_ENV,
  DEFAULT_NOW_PROVIDER,
  DEFAULT_SCOPE,
  IDP_ENV_ORIGIN_MAP,
  INVALID_REFRESH_TOKEN_ERROR_MESSAGE,
  IS_AUTHENTICATED_CACHED_TIME_MS,
  MAX_TIMEOFF,
  MISSING_REFRESH_TOKEN_ERROR_MESSAGE,
  PKCE_CODE_VERIFIER,
  POPUP_PKCE_CODE_VERIFIER,
  RECOVERABLE_ERRORS
} from './constants';

import {
  AuthClientOptions,
  AuthResultOption,
  AuthorizeOptions,
  BaseLoginOptions,
  CacheLocation,
  GetTokenSilentlyOptions,
  GetTokenSilentlyResult,
  GetTokenSilentlyVerboseResponse,
  LoggingOptions,
  LogoutOptions,
  LogoutUrlOptions,
  OAuthTokenOptions,
  PopUpWindowOptions,
  PopupLoginOptions,
  RedirectLoginOptions,
  RedirectLoginResult,
  RefreshTokenOptions,
  TokenEndpointResponse
} from './global';

import { CacheKeyManifest } from './cache/key-manifest';
import { retryPromise, singlePromise } from './promise-utils';
import { isIE11 } from './user-agent';
// @ts-ignore
import TokenWorker from './worker/token.worker.ts';

/**
 * @ignore
 */
const lock = new Lock();

/**
 * @ignore
 */
const GET_TOKEN_SILENTLY_LOCK_KEY = 'auth.lock.getTokenSilently';

/**
 * @ignore
 */
const cacheLocationBuilders: Record<string, () => ICache> = {
  memory: () => new InMemoryCache().enclosedCache,
  sessionstorage: () => new SessionStorageCache(),
  localstorage: () => new LocalStorageCache()
};

/**
 * @ignore
 */
const cacheFactory = (location: string) => {
  return cacheLocationBuilders[location];
};

/**
 * @ignore
 */
const supportWebWorker = () => !isIE11();

/**
 * @ignore
 */
const getCustomInitialOptions = (
  options: AuthClientOptions
): BaseLoginOptions => {
  const {
    advancedOptions,
    audience,
    authClient,
    authorizeTimeoutInSeconds,
    cacheLocation,
    client_id,
    issuer,
    redirect_uri,
    scope,
    useRefreshTokens,
    useFormData,
    ...customParams
  } = options;
  return customParams;
};

/**
 * Identity Web SDK for Single Page Applications using [Authorization Code Grant Flow with PKCE]
 */
export default class AuthClient {
  private cacheManager: CacheManager;
  private customOptions: BaseLoginOptions;
  private popUpOptions: PopUpWindowOptions;
  private domainUrl: string;
  private defaultScope: string;
  private scope: string;
  private nowProvider: () => number | Promise<number>;

  cacheLocation: CacheLocation;
  private worker: Worker;
  private env: string;
  private loggingOptions: LoggingOptions;
  private isloginWithRedirect = false;
  private memoryStorage: ICache;
  private refreshTokenFailedCounter: number;
  private blockRefreshTokenForTime: number;
  private maxBlockTime: number;
  private cacheIsAuthenticatedTime: number;
  private isAuthenticatedCachedState = false;

  constructor(private options: AuthClientOptions) {
    validateOptions(options);

    typeof window !== 'undefined' && validateCrypto();

    let cache: ICache;

    this.cacheLocation = options.cacheLocation || CACHE_LOCATION_MEMORY;

    if (!cacheFactory(this.cacheLocation)) {
      throw new Error(`Invalid cache location "${this.cacheLocation}"`);
    }
    cache = cacheFactory(this.cacheLocation)();

    this.memoryStorage =
      this.cacheLocation !== 'memory' ? cacheFactory('memory')() : cache;

    this.popUpOptions = {
      intervalId: null,
      timeoutId: null
    };

    this.env = options.env ?? DEFAULT_ENV;
    this.scope =
      options.scope !== undefined ? options.scope + ' openid' : DEFAULT_SCOPE;

    this.nowProvider = this.options.nowProvider || DEFAULT_NOW_PROVIDER;

    this.cacheManager = new CacheManager(
      cache,
      !cache.allKeys
        ? new CacheKeyManifest(cache, this.options.client_id)
        : null,
      this.nowProvider
    );

    this.refreshTokenFailedCounter = 0;
    this.blockRefreshTokenForTime = Date.now();
    this.domainUrl = getBaseUrl(this.env);
    this.maxBlockTime = Date.now() + 1000 * MAX_TIMEOFF;
    this.cacheIsAuthenticatedTime = 0;

    // Don't use web workers unless using refresh tokens in memory and not IE11
    if (
      typeof window !== 'undefined' &&
      window.Worker &&
      this.options.useRefreshTokens &&
      this.cacheLocation === CACHE_LOCATION_MEMORY &&
      supportWebWorker()
    ) {
      this.worker = new TokenWorker();
    }

    this.customOptions = getCustomInitialOptions(options);

    this.loggingOptions = {
      method: '',
      isMethodDeprecated: false,
      cacheLocation: this.cacheLocation,
      useRefreshTokens: this.options.useRefreshTokens
    };
  }

  private _url(env: string, path: string) {
    return `${getBaseUrl(env)}${path}`;
  }

  private _getParams(
    authorizeOptions: BaseLoginOptions,
    state: string,
    nonce: string,
    code_challenge: string,
    redirect_uri: string
  ): AuthorizeOptions {
    const {
      useRefreshTokens,
      useFormData,
      authClient,
      cacheLocation,
      advancedOptions,
      detailedResponse,
      ...withoutClientOptions
    } = this.options;

    return {
      client_id: withoutClientOptions.client_id,
      authoptions: authorizeOptions.authoptions,
      audience: authorizeOptions.audience,
      scope: this.scope,
      response_type:
        authorizeOptions.response_type || this.options.response_type || 'code',
      state,
      nonce,
      redirect_uri: redirect_uri || this.options.redirect_uri,
      code_challenge,
      code_challenge_method: 'S256',
      prompt: authorizeOptions.prompt || this.options.prompt,
      response_mode:
        authorizeOptions.response_mode || this.options.response_mode
    };
  }
  private _authorizeUrl(authorizeOptions: AuthorizeOptions) {
    return this._url(
      this.env,
      `/authorize?${createQueryParams(authorizeOptions)}`
    );
  }

  private async _verifyAccessToken(access_token: string, expires_in: number) {
    const now = await this.nowProvider();

    return verifyToken({
      access_token,
      expires_in
    });
  }

  /**
   * ```js
   * await auth.buildAuthorizeUrl(options);
   * ```
   *
   * Builds an `/authorize` URL for loginWithRedirect using the parameters
   * provided as arguments. Random and secure `state` and `nonce`
   * parameters will be auto-generated.
   *
   * @param options
   */
  public async buildAuthorizeUrl(
    options: RedirectLoginOptions = {},
    isLogInRequest?: boolean,
    isLoginWithPopupRequest?: boolean
  ): Promise<string> {
    const { redirect_uri, appState, state, authoptions, ...authParams } =
      options;
    const authorizeOptions = {
      ...authParams,
      ...(authoptions || this.options.authoptions
        ? {
            authoptions: authoptions
              ? JSON.stringify(authoptions)
              : JSON.stringify(this.options.authoptions)
          }
        : {})
    };
    const stateIn = state || encode(createRandomString());
    const nonceIn = encode(createRandomString());
    const code_verifier = createRandomString();
    const code_challengeBuffer = await sha256(code_verifier);
    if (options.response_type !== 'id_token') {
      isLogInRequest
        ? localStorage.setItem(
            isLoginWithPopupRequest
              ? POPUP_PKCE_CODE_VERIFIER
              : PKCE_CODE_VERIFIER,
            code_verifier
          )
        : this.memoryStorage.set(PKCE_CODE_VERIFIER, code_verifier);
    }
    let appStateStr = '';
    try {
      if (appState && typeof appState === 'object') {
        appStateStr = JSON.stringify(appState) || '';
      } else {
        appStateStr = (appState || '').toString();
      }
    } catch (error) {
      console.error('Error stringifying appState:', error);
      // Optionally set a default value if stringifying fails
      appStateStr = '';
    }
    localStorage.setItem('app_state', appStateStr);
    const code_challenge = bufferToBase64UrlEncoded(code_challengeBuffer);
    const fragment = options.fragment ? `#${options.fragment}` : '';

    const params = this._getParams(
      authorizeOptions,
      stateIn,
      nonceIn,
      code_challenge,
      redirect_uri
    );
    const url = this._authorizeUrl(params);

    return url + fragment;
  }

  /**
   * ```js
   * try {
   *  await auth.loginWithPopup(options);
   * } catch(e) {
   *  if (e instanceof PopupCancelledError) {
   *    // Popup was closed before login completed
   *  }
   * }
   * ```
   *
   * Opens a popup with the `/authorize` URL using the parameters
   * provided as arguments. Random and secure `state` and `nonce`
   * parameters will be auto-generated. If the response is successful,
   * results will be valid according to their expiration times.
   *
   * IMPORTANT: This method has to be called from an event handler
   * that was started by the user like a button click, for example,
   * otherwise the popup will be blocked in most browsers.
   *
   */
  public async loginWithPopup(
    options: PopupLoginOptions = {}
  ): Promise<string> {
    const stateIn = options.state
      ? options.state
      : encode(createRandomString());

    this.cleanupPopup(true);

    const redirect_uri =
      options.redirect_uri ||
      this.options.redirect_uri_popup ||
      this.options.redirect_uri;

    const urlOptions = {
      ...options,
      state: stateIn,
      redirect_uri: redirect_uri
    };

    const url = await this.buildAuthorizeUrl(urlOptions, true, true);

    this.popUpOptions.popupWindow = openPopup(url);

    if (!this.popUpOptions.popupWindow) {
      throw GenericError.fromPayload({
        error: 'open_popup_failed',
        error_description:
          'Unable to open a popup for loginWithPopup - window.open returned `null`'
      });
    }

    const codeResult = await runPopup(
      this.popUpOptions,
      redirect_uri,
      this.cleanupPopup.bind(this)
    );

    if (stateIn !== codeResult.state) {
      throw GenericError.fromPayload({
        error: 'invalid_state',
        error_description: 'Invalid State'
      });
    }

    try {
      this.loggingOptions.method = 'loginWithPopup';
      this.loggingOptions.isMethodDeprecated = false;

      const authResult = await oauthToken(
        {
          ...this.customOptions,
          grant_type: 'authorization_code',
          client_id: this.options.client_id,
          code_verifier: localStorage.getItem(POPUP_PKCE_CODE_VERIFIER),
          code: codeResult.code,
          scope: options.scope || this.scope,
          audience: options.audience || this.options.audience || 'default',
          baseUrl: this.domainUrl,
          redirect_uri: redirect_uri
        } as OAuthTokenOptions,
        this.worker,
        this.loggingOptions
      );

      localStorage.removeItem(POPUP_PKCE_CODE_VERIFIER);

      this.loggingOptions.method = '';
      this.loggingOptions.isMethodDeprecated = false;

      const decodedToken = await this._verifyAccessToken(
        authResult.access_token,
        authResult.expires_in
      );

      if (!this.options.useRefreshTokens) {
        authResult.refresh_token = undefined;
      }

      const cacheEntry = {
        ...authResult,
        decodedToken,
        audience: options.audience || this.options.audience || 'default',
        scope: options.scope || this.scope,
        ...(authResult.scope ? { oauthTokenScope: authResult.scope } : null),
        client_id: this.options.client_id
      };

      await this.cacheManager.set(cacheEntry);

      if (authResult.access_token) {
        localStorage.setItem('auth.session', 'true');
      }

      return authResult.access_token;
    } catch (e) {
      this.loggingOptions.method = '';
      this.loggingOptions.isMethodDeprecated = false;

      throw GenericError.fromPayload({
        error: 'failed_to_fetch_token',
        error_description:
          e.message || e.error_description || 'Failed to fetch token'
      });
    }
  }

  /**
   * ```js
   * await auth.closePopup();
   * ```
   *
   * Closes the popup instance that is already open
   * If no popup instance available, it will throw an error - 'close_popup_failed'
   *
   */
  public async closePopup() {
    if (this.popUpOptions.loginWithPopupPromiseHandlers)
      this.popUpOptions.loginWithPopupPromiseHandlers.reject(
        new PopupCancelledError()
      );
    this.cleanupPopup();
  }

  private cleanupPopup(isCalledInternally = false) {
    if (this.popUpOptions.popupWindow) {
      this.popUpOptions.popupWindow.close();
    } else if (!isCalledInternally) {
      throw GenericError.fromPayload({
        error: 'close_popup_failed',
        error_description: 'There is no popup instance available to be closed'
      });
    }

    if (this.popUpOptions.intervalId) {
      clearInterval(this.popUpOptions.intervalId);
    }

    if (this.popUpOptions.timeoutId) {
      clearTimeout(this.popUpOptions.timeoutId);
    }

    this.popUpOptions.intervalId = null;
    this.popUpOptions.timeoutId = null;
    this.popUpOptions.popupWindow = null;
    this.popUpOptions.loginWithPopupPromiseHandlers = undefined;
  }

  /**
   * ```js
   * await auth.loginWithRedirect(options);
   * ```
   *
   * Performs a redirect to `/authorize` using the parameters
   * provided as arguments. Random and secure `state` and `nonce`
   * parameters will be auto-generated.
   *
   * @param options
   */
  public async loginWithRedirect(options: RedirectLoginOptions = {}) {
    if (this.popUpOptions?.popupWindow) {
      console.log(
        'Popup closed due to simultaneous invokation of loginWithRedirect Request'
      );
      this.closePopup();
    }

    if (this.isloginWithRedirect) {
      console.log('A loginWithRedirect request is already in progress.');
      return;
    }

    this.isloginWithRedirect = true;
    const { redirectMethod, redirectTarget, ...urlOptions } = options;
    const url = await this.buildAuthorizeUrl(urlOptions, true);
    const target = redirectTarget ?? window.location;
    target[redirectMethod || 'assign'](url);
    this.isloginWithRedirect = false;
  }

  /**
   * After successful login, authorization server will redirect the user to
   * provided redirect_uri. It also adds authorization code and state to the query parameter in URL
   * call `handleRedirectCallback` to fetch the token using the code received in the query parameter.
   * This will populate the cache with tokens and getAccessTokenSilently call will return the token from cache after this.
   * Calling this method on page load, after successful login, will avoid getting the token from iframe as the cache
   * will already get populated with the token.
   */
  public async handleRedirectCallback(
    url: string = window.location.href
  ): Promise<RedirectLoginResult> {
    const queryStringFragments = url.split('?').slice(1);

    if (queryStringFragments.length === 0) {
      throw new Error('There are no query params available for parsing.');
    }

    const { state, code, error, error_description } = parseQueryResult(
      queryStringFragments.join('')
    );

    // Local Storage should have a `code_verifier` to do PKCE Verification
    if (!localStorage.getItem(PKCE_CODE_VERIFIER)) {
      throw new Error('Invalid PKCE code verifier');
    }
    const appStateStr = localStorage.getItem('app_state');

    let appState;

    if (appStateStr) {
      try {
        appState = JSON.parse(appStateStr);
      } catch (e) {
        appState = appStateStr;
      }
    }

    if (error) {
      throw new AuthenticationError(error, error_description, state, appState);
    }

    const tokenOptions = {
      audience: this.options.audience || 'default',
      scope: this.scope,
      baseUrl: this.domainUrl,
      client_id: this.options.client_id,
      code_verifier: localStorage.getItem(PKCE_CODE_VERIFIER),
      grant_type: 'authorization_code',
      code
    } as OAuthTokenOptions;

    if (undefined !== this.options.redirect_uri) {
      tokenOptions.redirect_uri = this.options.redirect_uri;
    }

    this.loggingOptions.method = 'handleRedirectCallback';
    this.loggingOptions.isMethodDeprecated = false;

    const authResult = await oauthToken(
      tokenOptions,
      this.worker,
      this.loggingOptions
    );

    localStorage.removeItem(PKCE_CODE_VERIFIER);

    this.loggingOptions.method = '';
    this.loggingOptions.isMethodDeprecated = false;

    const decodedToken = await this._verifyAccessToken(
      authResult.access_token,
      authResult.expires_in
    );

    if (!this.options.useRefreshTokens) {
      authResult.refresh_token = undefined;
    }

    await this.cacheManager.set({
      ...authResult,
      decodedToken,
      audience: this.options.audience || 'default',
      scope: this.scope,
      ...(authResult.scope ? { oauthTokenScope: authResult.scope } : null),
      client_id: this.options.client_id
    });

    if (authResult.access_token) {
      localStorage.setItem('auth.session', 'true');
    }

    return {
      appState: appState,
      accessToken: authResult.access_token
    };
  }

  public async getAccessTokenSilently(
    options: GetTokenSilentlyOptions = {}
  ): Promise<string> {
    const getTokenOptions = {
      audience: this.options.audience,
      ...options,
      scope: getUniqueScopes(options.scope || this.scope)
    };
    this.loggingOptions.method = 'getAccessTokenSilently';
    return singlePromise(
      () => this._getTokenSilently('accessToken', getTokenOptions),
      `${this.options.client_id}::${getTokenOptions.audience}::${getTokenOptions.scope}::AccessToken`
    );
  }

  public async getIDTokenSilently(
    options: GetTokenSilentlyOptions = {}
  ): Promise<string> {
    const getTokenOptions = {
      audience: this.options.audience,
      ...options,
      scope: getUniqueScopes(options.scope || this.scope)
    };
    this.loggingOptions.method = 'getIDTokenSilently';
    this.loggingOptions.isMethodDeprecated = false;

    return singlePromise(
      () => this._getTokenSilently('idToken', getTokenOptions),
      `${this.options.client_id}::${getTokenOptions.audience}::${getTokenOptions.scope}::IDToken`
    );
  }

  private async _getTokenSilently(
    authResultOption: AuthResultOption = 'accessToken',
    options: GetTokenSilentlyOptions = {}
  ): Promise<string | GetTokenSilentlyVerboseResponse> {
    const cacheKeyParams = {
      scope: options.scope || this.scope,
      audience: options.audience || this.options.audience || 'default',
      client_id: this.options.client_id
    };
    const getAccessTokenFromCache = async () => {
      const cache = await this.cacheManager.get(
        new CacheKey(cacheKeyParams),
        60 // get a new token if within 60 seconds of expiring
      );

      return cache;
    };
    let idTokenFromCache: string | null = null;
    const cache = await getAccessTokenFromCache();

    if (options.response_type === 'id_token') {
      try {
        const id_token_result = await this._getTokenFromIFrame(options);
        if (cache) {
          await this.cacheManager.set(
            {
              ...cache,
              ...cacheKeyParams,
              id_token: id_token_result.id_token
            },
            true
          );
        } else {
          await this.cacheManager.set({
            ...cacheKeyParams,
            id_token: id_token_result.id_token
          });
        }
        return id_token_result.id_token;
      } catch (e) {
        throw e;
      }
    }

    if (cache) {
      if ('accessToken' === authResultOption && cache?.access_token) {
        return cache.access_token;
      }

      if ('idToken' === authResultOption && cache?.id_token) {
        return cache.id_token;
      }

      if (this.options.useRefreshTokens && cache.refresh_token) {
        idTokenFromCache = cache.id_token ? cache.id_token : null;
        const authResultFromRefreshCall = await this._getTokenUsingRefreshToken(
          options
        );
        const authResult = {
          ...authResultFromRefreshCall,
          id_token: idTokenFromCache
        };
        await this.cacheManager.set({
          client_id: this.options.client_id,
          ...cacheKeyParams,
          ...authResult
        });

        if ('accessToken' === authResultOption) {
          return authResult.access_token;
        }

        if ('idToken' === authResultOption) {
          return authResult.id_token;
        }
      }
    }

    if (
      await retryPromise(
        () => lock.acquireLock(GET_TOKEN_SILENTLY_LOCK_KEY, 5000),
        10
      )
    ) {
      try {
        // Check the cache a second time, because it may have been populated
        // by a previous call while this call was waiting to acquire the lock.
        const cache = await getAccessTokenFromCache();

        if ('accessToken' === authResultOption && cache?.access_token) {
          return cache.access_token;
        }

        if ('idToken' === authResultOption && cache?.id_token) {
          return cache.id_token;
        }

        // Get IDToken from cache if it exists as refresh token call does not return IDToken
        idTokenFromCache = cache && cache.id_token ? cache.id_token : null;
        let authResult = this.options.useRefreshTokens
          ? await this._getTokenUsingRefreshToken(options)
          : await this._getTokenFromIFrame(options);

        if (!authResult.id_token && idTokenFromCache !== null) {
          authResult = {
            ...authResult,
            id_token: idTokenFromCache
          };
        }

        if (!this.options.useRefreshTokens) {
          authResult.refresh_token = undefined;
        }

        await this.cacheManager.set({
          client_id: this.options.client_id,
          ...cacheKeyParams,
          ...authResult
        });

        if (
          options.detailedResponse ||
          'detailedResponse' === authResultOption
        ) {
          const { id_token, access_token, oauthTokenScope, expires_in } =
            authResult;

          return {
            id_token,
            access_token,
            ...(oauthTokenScope ? { scope: oauthTokenScope } : null),
            expires_in
          };
        }

        if (authResult.access_token) {
          localStorage.setItem('auth.session', 'true');
        }

        if ('idToken' === authResultOption) {
          return authResult.id_token;
        }

        return authResult.access_token;
      } finally {
        await lock.releaseLock(GET_TOKEN_SILENTLY_LOCK_KEY);
      }
    } else {
      throw new TimeoutError();
    }
  }

  /**
   * ```js
   * const isAuthenticated = await auth.isAuthenticated();
   * ```
   *
   * Returns `true` if there's valid session,
   * otherwise returns `false`.
   *
   */
  public async isAuthenticated() {
    // Return isAuthenticated call with cached value if called within 5 seconds
    if (Date.now() < this.cacheIsAuthenticatedTime) {
      return this.isAuthenticatedCachedState;
    }

    const options = {
      response_mode: 'fragment',
      response_type: 'id_token'
    };
    const getTokenOptions = {
      audience: this.options.audience,
      ...options,
      scope: getUniqueScopes(this.scope)
    };

    const isAuthenticated = await singlePromise(
      () => this._getTokenSilently('idToken', getTokenOptions),
      `${this.options.client_id}::${getTokenOptions.audience}::${getTokenOptions.scope}::isAuthenticated`
    )
      .then(token => {
        return !!token;
      })
      .catch(e => {
        if (!RECOVERABLE_ERRORS.includes(e.error)) {
          throw e;
        }
        // Gracefully handling error to return false for isAuthenticated
        console.log(
          'Error occured- error:' +
            e.error +
            ' error_description:' +
            e.error_description
        );
        return false;
      });

    this.isAuthenticatedCachedState = isAuthenticated;
    this.cacheIsAuthenticatedTime =
      Date.now() + IS_AUTHENTICATED_CACHED_TIME_MS * 1000;

    return isAuthenticated;
  }

  /**
   * ```js
   * await auth.buildLogoutUrl(options);
   * ```
   *
   * Builds a URL to the logout endpoint using the parameters provided as arguments.
   * @param options
   */
  public buildLogoutUrl(options: LogoutUrlOptions = {}): string {
    options.client_id = options.client_id || this.options.client_id;
    const url = this._url(this.env, `/logout?${createQueryParams(options)}`);
    return url;
  }

  /**
   * ```js
   * auth.logout();
   * ```
   *
   * Clears the application session and performs a redirect to `/v2/logout`, using
   * the parameters provided as arguments, to clear the Auth session.
   *
   * **Note:** If you are using a custom cache, and specifying `localOnly: true`, and you want to perform actions or read state from the SDK immediately after logout, you should `await` the result of calling `logout`.
   *
   * If the `localOnly` option is specified, it only clears the application session.
   *
   * @param options
   */
  public async logout(options: LogoutOptions = {}) {
    const { localOnly, ...logoutOptions } = options;

    const getAccessTokenFromCache = async () => {
      const cache = await this.cacheManager.get(
        new CacheKey({
          scope: this.scope,
          audience: this.options.audience || 'default',
          client_id: this.options.client_id
        }),
        0,
        true
      );
      return cache;
    };

    const cache = await getAccessTokenFromCache();
    //invalidate tokens
    if (cache) {
      const revokeRequests = [];

      if (cache.access_token) {
        revokeRequests.push(
          this._invalidateToken(cache.access_token, 'access_token')
        );
      }

      if (cache.refresh_token) {
        revokeRequests.push(
          this._invalidateToken(cache.refresh_token, 'refresh_token')
        );
      }

      await Promise.all(revokeRequests);
    }

    const postCacheClear = () => {
      localStorage.removeItem(PKCE_CODE_VERIFIER);
      localStorage.removeItem(POPUP_PKCE_CODE_VERIFIER);
      localStorage.removeItem('app_state');
      localStorage.removeItem('auth.session');
      this.memoryStorage.remove(PKCE_CODE_VERIFIER);

      if (localOnly) {
        return;
      }

      const url = this.buildLogoutUrl(logoutOptions);

      window.location.assign(url);
    };

    this.cacheManager.clearSync();
    postCacheClear();
  }

  private async _getTokenFromIFrame(
    options: GetTokenSilentlyOptions
  ): Promise<GetTokenSilentlyResult> {
    let paramsRedirectUri =
      this.options.redirect_uri_iframe ||
      options.redirect_uri ||
      this.options.redirect_uri ||
      window.location.origin;

    const url =
      (await this.buildAuthorizeUrl({
        ...options,
        redirect_uri: paramsRedirectUri
      })) + '&prompt=none';

    const timeout =
      options.timeoutInSeconds || this.options.authorizeTimeoutInSeconds;

    try {
      // When a browser is running in a Cross-Origin Isolated context, using iframes is not possible.
      // It doesn't throw an error but times out instead, so we should exit early and inform the user about the reason.
      // https://developer.mozilla.org/en-US/docs/Web/API/crossOriginIsolated
      if ((window as any).crossOriginIsolated) {
        throw new GenericError(
          'login_required',
          'The application is running in a Cross-Origin Isolated context, silently retrieving a token without refresh token is not possible.'
        );
      }

      const idpOrigin = IDP_ENV_ORIGIN_MAP[this.env];
      const codeResult = await runIframe(
        url,
        this.domainUrl,
        paramsRedirectUri,
        idpOrigin,
        timeout
      );
      if (codeResult.id_token) {
        return {
          id_token: codeResult.id_token
        };
      }

      const {
        code,
        scope,
        audience,
        redirect_uri,
        timeoutInSeconds,
        detailedResponse,
        ...customOptions
      } = options;

      const tokenResult = await oauthToken(
        {
          ...this.customOptions,
          grant_type: 'authorization_code',
          client_id: this.options.client_id,
          code_verifier: this.memoryStorage.get(PKCE_CODE_VERIFIER),
          code: codeResult.code,
          scope: options.scope || this.scope,
          audience: options.audience || this.options.audience || 'default',
          baseUrl: this.domainUrl,
          redirect_uri: paramsRedirectUri,
          ...customOptions
        } as OAuthTokenOptions,
        this.worker,
        this.loggingOptions
      );

      this.memoryStorage.remove(PKCE_CODE_VERIFIER);

      this.loggingOptions.method = '';
      this.loggingOptions.isMethodDeprecated = false;

      const decodedToken = await this._verifyAccessToken(
        tokenResult.access_token,
        tokenResult.expires_in
      );

      return {
        ...tokenResult,
        decodedToken,
        scope: options.scope || this.scope,
        oauthTokenScope: tokenResult.scope,
        audience: options.audience || this.options.audience || 'default'
      };
    } catch (e) {
      if (e.error === 'login_required') {
        this.logout({
          localOnly: true
        });
      }
      throw e;
    }
  }

  private async _getTokenUsingRefreshToken(
    options: GetTokenSilentlyOptions
  ): Promise<GetTokenSilentlyResult> {
    options.scope = getUniqueScopes(
      this.defaultScope,
      this.options.scope,
      options.scope
    );

    const cache = await this.cacheManager.get(
      new CacheKey({
        scope: options.scope || this.scope,
        audience: options.audience || this.options.audience || 'default',
        client_id: this.options.client_id
      })
    );

    // If you don't have a refresh token in memory
    // and you don't have a refresh token in web worker memory
    // fallback to an iframe.
    if (!cache || !cache.refresh_token) {
      return await this._getTokenFromIFrame(options);
    }

    const redirect_uri =
      options.redirect_uri ||
      this.options.redirect_uri ||
      window.location.origin;

    let tokenResult: TokenEndpointResponse;

    const {
      scope,
      audience,
      timeoutInSeconds,
      detailedResponse,
      ...customOptions
    } = options;

    const timeout =
      typeof options.timeoutInSeconds === 'number'
        ? options.timeoutInSeconds * 1000
        : null;

    try {
      if (this.refreshTokenFailedCounter >= BLOCK_REFRESH_TOKEN_CALL_ATTEMPTS) {
        if (this.blockRefreshTokenForTime > Date.now()) {
          throw new Error(BLOCKED_REFRESH_TOKEN_CALL_ERROR);
        }
        if (Date.now() >= this.maxBlockTime) {
          this.refreshTokenFailedCounter = 0;
        }
      }

      tokenResult = await oauthToken(
        {
          ...this.customOptions,
          ...customOptions,
          grant_type: 'refresh_token',
          refresh_token: cache.refresh_token,
          client_id: this.options.client_id,
          scope: options.scope || this.scope,
          audience: options.audience || this.options.audience || 'default',
          baseUrl: this.domainUrl,
          redirect_uri: redirect_uri,
          ...(timeout && { timeout })
        } as RefreshTokenOptions,
        this.worker,
        this.loggingOptions
      );
    } catch (e) {
      this.loggingOptions.method = '';
      this.loggingOptions.isMethodDeprecated = false;
      if (
        // The web worker didn't have a refresh token in memory so
        // fallback to an iframe.
        e.message === MISSING_REFRESH_TOKEN_ERROR_MESSAGE ||
        // A refresh token was found, but is it no longer valid.
        // Fallback to an iframe.
        (e.message &&
          e.message.indexOf(INVALID_REFRESH_TOKEN_ERROR_MESSAGE) > -1)
      ) {
        this.refreshTokenFailedCounter = 0;
        return await this._getTokenFromIFrame(options);
      }
      if (this.blockRefreshTokenForTime < Date.now()) {
        this.refreshTokenFailedCounter += 1;
        if (
          this.refreshTokenFailedCounter >= BLOCK_REFRESH_TOKEN_CALL_ATTEMPTS
        ) {
          const temp = Math.min(
            MAX_TIMEOFF,
            BASE_JITTER * 2 ** this.refreshTokenFailedCounter
          );
          let blocked_time = temp / 2 + Math.random() * (temp / 2);
          this.blockRefreshTokenForTime = Date.now() + 1000 * blocked_time;
          this.maxBlockTime = Date.now() + 1000 * MAX_TIMEOFF;
        }
      }
      throw e;
    }

    this.loggingOptions.method = '';
    this.loggingOptions.isMethodDeprecated = false;

    const decodedToken = await this._verifyAccessToken(
      tokenResult.access_token,
      tokenResult.expires_in
    );
    this.refreshTokenFailedCounter = 0;

    return {
      ...tokenResult,
      decodedToken,
      scope: options.scope || this.scope,
      oauthTokenScope: tokenResult.scope,
      audience: options.audience || this.options.audience || 'default'
    };
  }

  private async _invalidateToken(
    token: string,
    tokenType: string
  ): Promise<any> {
    const url = this.domainUrl + '/revoke';
    const body = {
      token: token,
      token_type_hint: tokenType,
      client_id: this.options.client_id
    };

    const reqbody = createQueryParams(body);

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          Accept: 'application/json, text/plain, */*'
        },
        body: reqbody
      });

      return response.statusText;
    } catch (error) {
      throw new Error('Failed to revoke token');
    }
  }

  /**
   * *
   * @param accessToken
   * @deprecated This function is being deprecated and will be removed in future. Please reach out to #userprofile-support channel on slack for more details.
   */
  public async getUserProfile(accessToken: string): Promise<any> {
    if (accessToken) {
      const url =
        this.domainUrl.split('/authentication')[0] +
        '/userprofile/v1/users/@me';
      try {
        const response = await fetch(url, {
          headers: {
            Authorization: 'Bearer ' + accessToken,
            'Content-Type': 'application/json'
          }
        });
        return await response.json();
      } catch (error) {
        throw new Error('Failed to fetch user profile');
      }
    } else {
      throw new Error('No access token was provided.');
    }
  }
}
